import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import "./style.css";
import App from "./App.vue";
import * as Sentry from "@sentry/vue";
import * as amplitude from "@amplitude/analytics-browser";
import en from "@scantrust/translations-web-video/locales/en.json";
import de from "@scantrust/translations-web-video/locales/de.json";
import es from "@scantrust/translations-web-video/locales/es.json";
import fa from "@scantrust/translations-web-video/locales/fa.json";
import fr from "@scantrust/translations-web-video/locales/fr.json";
import hi from "@scantrust/translations-web-video/locales/hi-IN.json";
import id from "@scantrust/translations-web-video/locales/id.json";
import it from "@scantrust/translations-web-video/locales/it.json";
import ja from "@scantrust/translations-web-video/locales/ja.json";
import ko from "@scantrust/translations-web-video/locales/ko.json";
import nl from "@scantrust/translations-web-video/locales/nl.json";
import th from "@scantrust/translations-web-video/locales/th.json";
import zh from "@scantrust/translations-web-video/locales/zh.json";
import zhTW from "@scantrust/translations-web-video/locales/zh-TW.json";
import zhHK from "@scantrust/translations-web-video/locales/zh-HK.json";
import zhMO from "@scantrust/translations-web-video/locales/zh-MO.json";
import vi from "@scantrust/translations-web-video/locales/vi.json";
import { applicationState } from "./utils/globalReferences.js";
import { initialLanguage } from "./utils/language.js";

// import for side effects only
import "./bootstrap/canary.js";

const app = createApp(App);

if (import.meta.env.MODE === "staging" || import.meta.env.MODE === "development") {
  import("eruda").then((module) => {
    module.default.init();
  });
}

const i18n = createI18n({
  legacy: false,
  locale: initialLanguage,
  fallbackLocale: initialLanguage,
  // hk and mo are needed for proper camera selection
  messages: { en, de, es, fa, fr, hi, id, it, ja, ko, nl, th, zh, "zh-TW": zhTW, "zh-HK": zhHK, "zh-MO": zhMO, vi }
});

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,
    integrations: [Sentry.browserTracingIntegration(), Sentry.httpClientIntegration()],
    sendDefaultPii: true, // required for httpClientIntegration
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      /^https:\/\/api\.scantrust\.com/, // this backend support sentry-trace header
      /^https:\/\/api\.staging.scantrust\.io/ // this backend support sentry-trace header
    ]
  });
}

if (import.meta.env.VITE_AMPLITUDE_API_KEY) {
  const currentHost = window.location.hostname;
  const reducedSampleRateHosts = import.meta.env.VITE_AMPLITUDE_REDUCED_SAMPLE_RATE_HOSTS?.split(",") || [];
  // The env files hold the only copy of this rate: 0 is honored, and anything unusable means no reduction.
  const reducedSampleRate = parseFloat(import.meta.env.VITE_AMPLITUDE_REDUCED_SAMPLE_RATE);
  const sampleRate = reducedSampleRateHosts.includes(currentHost) && reducedSampleRate >= 0 && reducedSampleRate <= 1 ? reducedSampleRate : 1;

  // The SDK has no event sampling, so opt the whole page load out instead. Per load, not per user.
  const inSample = Math.random() < sampleRate;

  amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
    // Every `false` is required: on an object the SDK treats a missing key as enabled.
    autocapture: {
      webVitals: true,
      attribution: false,
      fileDownloads: false,
      formInteractions: false,
      pageUrlEnrichment: false,
      pageViews: false,
      sessions: false
    },
    optOut: !inSample
  });
}

app.use(i18n);
app.mount("#app");
